<template>
  <div class="enter-root" :class="{'enter-root--room': published}">
    <template v-if="!published">
      <div v-if="loading" class="enter-loader">
        <div class="enter-loader__inner">
          <div class="enter-loader__ring">
            <div /><div /><div /><div />
          </div>
        </div>
      </div>

      <template v-else>
        <VideoRoomHeader />
        <div class="enter-bg">
          <div v-if="step === 1" class="enter-card">
            <div class="enter-card__header">
              <span class="enter-card__tag">{{ $t('tag') }}</span>
              <h1 class="enter-card__title">
                {{ $t('title') }}
              </h1>
            </div>
            <div class="enter-card__body">
              <VideoRoomEnterInfo />
            </div>
            <div class="enter-card__footer">
              <button class="enter-btn" @click="step = 2">
                {{ $t('next') }}
                <img src="../assets/img/icons/chevron-right.svg" width="16" height="16" alt="" />
              </button>
            </div>
          </div>
          <VideoRoomEnterForm v-else />
        </div>
      </template>
    </template>

    <VideoRoom v-else />
  </div>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import useRoom from '@/compositions/useRoom';
import { UserType } from '@/enums';
import VideoRoomEnterInfo from '@/components/VideoRoomEnterInfo.vue';
import VideoRoomHeader from '@/components/VideoRoomHeader.vue';
import VideoRoomEnterForm from '@/components/VideoRoomEnterForm.vue';
import useJanusPublisher from '@/compositions/useJanusPublisher';
import VideoRoom from '@/components/VideoRoom.vue';

export default {
  name: 'VideoRoomEnter',
  components: {
    VideoRoom,
    VideoRoomEnterForm,
    VideoRoomHeader,
    VideoRoomEnterInfo,
  },
  setup() {
    const { user, existingRoom } = useRoom();
    const { published } = useJanusPublisher();

    const step = ref(1);
    const loading = computed(() => !existingRoom.value);

    return {
      user,
      UserType,
      step,
      published,
      loading,
    };
  },
};
</script>

<style lang="scss" scoped>
.enter-root {
  min-height: 100vh;
  background: #F0F4F8;

  &--room {
    min-height: unset;
    background: none;
    overflow: hidden;
  }
}

.enter-loader {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(160deg, #EBF4FF 0%, #F0F8FF 100%);
  z-index: 100;
}

.enter-loader__inner {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.enter-loader__ring {
  display: inline-block;
  position: relative;
  width: 56px;
  height: 56px;

  div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 44px;
    height: 44px;
    margin: 6px;
    border: 4px solid transparent;
    border-radius: 50%;
    animation: ring-spin 1.1s cubic-bezier(0.5, 0, 0.5, 1) infinite;

    &:nth-child(1) { animation-delay: -0.45s; border-top-color: #0A82D9; }
    &:nth-child(2) { animation-delay: -0.3s;  border-top-color: #1aA8E8; }
    &:nth-child(3) { animation-delay: -0.15s; border-top-color: #00CBF3; }
    &:nth-child(4) { border-top-color: #60DDF8; }
  }
}

@keyframes ring-spin {
  0%   { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.enter-bg {
  min-height: calc(100vh - 4rem);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem 3rem;
  background: linear-gradient(160deg, #F0F4F8 0%, #EAF1F8 100%);
}

.enter-card {
  width: 100%;
  max-width: 45rem;
  background: #FFFFFF;
  border-radius: 1rem;
  box-shadow: 0 4px 24px rgba(10, 130, 217, 0.08), 0 1px 4px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  text-align: left;

  &__header {
    padding: 2rem 2.5rem 1.5rem;
    border-bottom: 1px solid #F0F4F8;
    background: linear-gradient(135deg, #F8FBFF 0%, #FFFFFF 100%);
  }

  &__tag {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #0A82D9;
    background: rgba(10, 130, 217, 0.08);
    border-radius: 4px;
    padding: 0.2rem 0.6rem;
    margin-bottom: 0.75rem;
  }

  &__title {
    font-size: 1.5rem;
    font-weight: 500;
    line-height: 1.25;
    color: #1A1A2E;
    margin: 0;
  }

  &__body {
    padding: 1.75rem 2.5rem;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    padding: 1.25rem 2.5rem 1.75rem;
    border-top: 1px solid #F0F4F8;
  }
}

.enter-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #FFFFFF;
  background: linear-gradient(135deg, #0A82D9 0%, #00CBF3 100%);
  border: none;
  border-radius: 8px;
  padding: 0.65rem 1.5rem;
  cursor: pointer;
  transition: box-shadow 0.25s ease, transform 0.15s ease;
  box-shadow: 0 3px 10px rgba(10, 130, 217, 0.35);

  svg {
    flex-shrink: 0;
  }

  &:hover {
    box-shadow: 0 5px 18px rgba(10, 130, 217, 0.45);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(10, 130, 217, 0.3);
  }

  &:focus {
    outline: none;
  }
}

@media (max-width: 640px) {
  .enter-bg {
    padding: 1rem 0.75rem;
    align-items: flex-start;
  }

  .enter-card {
    &__header {
      padding: 1.5rem 1.5rem 1.25rem;
    }

    &__body {
      padding: 1.25rem 1.5rem;
    }

    &__footer {
      padding: 1rem 1.5rem 1.5rem;
      justify-content: stretch;
    }
  }

  .enter-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>

<i18n locale="en" lang="yaml">
title: Interview
tag: Video interview
next: Next
</i18n>

<i18n locale="ru" lang="yaml">
title: Собеседование
tag: Видеособеседование
next: Далее
</i18n>
